import $ from 'jquery';
import Cookies from 'js-cookie';
import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import GLightbox from 'glightbox';

import { $Elements, Classes, Durations, Elements, Events, ScrollAttributes, SmoothScroll } from './constants';

// ---------- Register GSAP Plugins ----------

gsap.registerPlugin(ScrollToPlugin, ScrollTrigger);

// ---------- Init Smooth Scrolling ----------

export let lenis = null;

if(SmoothScroll) {

	lenis = new Lenis({
		duration: 2,
		prevent: (node) => node.localName === 'feedbucket-app',
	});

	lenis.on('scroll', ScrollTrigger.update);

	gsap.ticker.add((time) => {
		lenis.raf(time * 1000)
	});

	gsap.ticker.lagSmoothing(0);
}

// petit état interne pour éviter de désactiver / réactiver plusieurs fois
let menuScrollLocked = false;

// ---------- Helpers ----------

export const wait = (delay, value) => new Promise(resolve => setTimeout(resolve, delay, value));

export function escapeHTML(strings) {

	let result = strings[0];

	for(let i = 1; i < arguments.length; i++) {
		result += String(arguments[i]).replace(/[&<>'"]/g, (c) => {
			return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
		});
		result += strings[i];
	}

	return result;
}

export function fixMobileHover() {

	if(ScrollTrigger.isTouch === 1) {

		const styleSheets = Array.from(document.styleSheets).filter(
			(styleSheet) => !styleSheet.href || styleSheet.href.startsWith(window.location.origin)
		);

		for(let style of styleSheets) {
			if(style instanceof CSSStyleSheet && style.cssRules) {
				for(let ruleI = style.cssRules.length - 1; ruleI >= 0; ruleI--) {
					let rule = style.cssRules[ruleI];
					if(rule.selectorText) {
						rule.selectorText = rule.selectorText.replace(':hover', ':active');
					}
				}
			}
		}
	}
}

export const mediaQueryString = (breakpoint, min = true) => (min) ? `(min-width: ${breakpoint}px)` : `(max-width: ${breakpoint - 1}px)`;

export function mediaQuery(breakpoint, before, after) {

	const handler = (query) => {
		if(typeof after === 'function' && query.matches) {
			after();
		}
		else if(typeof before === 'function') {
			before();
		}
	};

	if(window.matchMedia) {
		const query = window.matchMedia(mediaQueryString(breakpoint));
		query.addListener(handler);
		handler(query);
	}
}

export function elementPosition(element) {

	const rect = element.getBoundingClientRect();

	const scroll = scrollPosition();

	return {
		top: Math.round(rect.top + scroll.scrollTop - scroll.clientTop),
		left: Math.round(rect.left + scroll.scrollLeft - scroll.clientLeft),
		height: rect.height,
		width: rect.width,
	};
}


export function scrollPosition() {

	const root = document.documentElement;
	const body = document.body;

	const scrollTop = window.pageYOffset || root.scrollTop || body.scrollTop || 0;
	const scrollLeft = window.pageXOffset || root.scrollLeft || body.scrollLeft || 0;

	const clientTop = root.clientTop || body.clientTop || 0;
	const clientLeft = root.clientLeft || body.clientLeft || 0;

	return {
		scrollTop: scrollTop,
		scrollLeft: scrollLeft,
		clientTop: clientTop,
		clientLeft: clientLeft
	};
}

export function smoothScrollTo(element, duration = Durations.animations.s, offset = Elements.HeaderWrapper.offsetHeight, scroller = window) {

	if(SmoothScroll) {

		lenis.scrollTo(element, {
			duration: duration,
			offset: -offset,
			immediate: (duration === 0),
			lock: true,
			force: true,
			easing: t => Math.sin((t * Math.PI) / 2),
		});
	}
	else {

		gsap.to(scroller, {
			scrollTo: {
				y: element,
				offsetY: offset,
				autoKill: false
			},
			duration: duration,
			overwrite: true,
		});
	}
}

export function disableBodyScroll() {
	const y = window.scrollY || document.documentElement.scrollTop || 0;

	// stop lenis
	if(SmoothScroll && lenis) {
		document.body.setAttribute(ScrollAttributes.scrollable, 'false');
		lenis.stop();
	}
	else {
		document.body.setAttribute(ScrollAttributes.scrolled, String(y));
	}

	// lock scroll (iOS + desktop safe)
	document.documentElement.classList.add(Classes.menuOpen);
	document.body.classList.add(Classes.menuOpen);

	// freeze body (works on iOS Safari)
	document.body.dataset.scrollY = String(y);
	document.body.style.position = 'fixed';
	document.body.style.top = `-${y}px`;
	document.body.style.left = '0';
	document.body.style.right = '0';
	document.body.style.width = '100%';
}


export function enableBodyScroll() {
	const y = parseInt(document.body.dataset.scrollY || '0', 10);

	// unfreeze
	document.body.style.position = '';
	document.body.style.top = '';
	document.body.style.left = '';
	document.body.style.right = '';
	document.body.style.width = '';
	delete document.body.dataset.scrollY;

	document.documentElement.classList.remove(Classes.menuOpen);
	document.body.classList.remove(Classes.menuOpen);

	// restore scroll
	window.scrollTo(0, y);

	// restart lenis if used
	if(SmoothScroll && lenis) {
		document.body.removeAttribute(ScrollAttributes.scrollable);
		lenis.start();
	}
	else {
		document.body.removeAttribute(ScrollAttributes.scrolled);
	}
}


export function toggleBodyScroll() {

	if(document.body.hasAttribute(ScrollAttributes.scrollable) || document.body.hasAttribute(ScrollAttributes.scrolled)) {
		enableBodyScroll();
	}
	else {
		disableBodyScroll();
	}
}

export function replaceBodyClasses(next) {

	const parser = new DOMParser();
	const newPage = parser.parseFromString(next.html, "text/html");
	const newBody = newPage.querySelector('body');

	document.body.className = newBody.getAttribute('class');
}

export function sortAnimations(animations) {

	let sortedAnimations = {};
	let animationsBreakpoints = {};

	Object.entries(animations).forEach(([breakpoint, value]) => {

		if(Array.isArray(value)) {
			sortedAnimations['all'] = animations.global;
		}
		else {
			sortedAnimations[mediaQueryString(value.width)] = (sortedAnimations[mediaQueryString(value.width)]) ? [...sortedAnimations[mediaQueryString(value.width)], ...value.min] : value.min;
			sortedAnimations[mediaQueryString(value.width, false)] = (sortedAnimations[mediaQueryString(value.width, false)]) ? [...sortedAnimations[mediaQueryString(value.width, false)], ...value.max] : value.max;
		}
	});

	Object.entries(sortedAnimations).forEach(([breakpoint, value]) => animationsBreakpoints[breakpoint] = () => value.map(animation => {
		try { return animation(); }
		catch(e) { console.warn('Animation init error:', e); }
	}));

	return animationsBreakpoints;
}

export function initGTM() {

	if(isCookiesAllowed()) {
		updateGTMConsent();
	}
}

export function updateGTMConsent() {

	if(typeof gtag === 'function') {

		gtag('consent', 'update', {
			'ad_storage': 'granted',
			'analytics_storage': 'granted',
			'ad_user_data': 'granted',
			'ad_personalization': 'granted',
			'personalization_storage': 'granted',
		});
	}
}

export function resetGTMConsent() {

	if(typeof gtag === 'function') {

		gtag('consent', 'update', {
			'ad_storage': 'denied',
			'analytics_storage': 'denied',
			'ad_user_data': 'denied',
			'ad_personalization': 'denied',
			'personalization_storage': 'granted',
		});
	}
}

export function setCookiesAllowed(consent) {

	const cookie = (typeof cookies_params !== 'undefined') ? cookies_params.global.cookies_allowed : 'tc-cookies-allowed';

	Cookies.set(cookie, consent, {
		expires: 365,
		path: '/',
	});

	if(consent === 'true') {
		updateGTMConsent();
	}
}

export function getCookiesAllowed() {

	const cookie = (typeof cookies_params !== 'undefined') ? cookies_params.global.cookies_allowed : 'tc-cookies-allowed';

	return Cookies.get(cookie);
}

export function resetCookiesAllowed() {

	const cookie = (typeof cookies_params !== 'undefined') ? cookies_params.global.cookies_allowed : 'tc-cookies-allowed';

	return Cookies.remove(cookie);
}

export function isCookiesAllowed() {
	return getCookiesAllowed() === 'true';
}

export function setDisclaimerAccepted() {

	const cookie = (typeof cookies_params !== 'undefined') ? cookies_params.global.disclaimer : 'tc-disclaimer-accepted';

	Cookies.set(cookie, true, {
		expires: 365,
		path: '/',
	});
}

export function getDisclaimerAccepted() {

	const cookie = (typeof cookies_params !== 'undefined') ? cookies_params.global.disclaimer : 'tc-disclaimer-accepted';

	return Cookies.get(cookie);
}

export function isDisclaimerAccepted() {
	return getDisclaimerAccepted() === 'true';
}


export function initLightboxes() {

	$('[target="lightbox"]').each(function() {

		if($(this).attr('data-size') === 'full') {

			GLightbox({
				selector: 'a[href="' + this.attributes.href.value + '"][target="lightbox"][data-size="full"]',
				openEffect: 'fade',
				closeEffect: 'fade',
				width: 'auto',
				height: 'auto',
				videosWidth: 1280,
				skin: 'fullscreen',
			});
		}
		else {

			GLightbox({
				selector: 'a[href="' + this.attributes.href.value + '"][target="lightbox"]',
				openEffect: 'fade',
				closeEffect: 'fade',
				videosWidth: 1280,
			});
		}
	});

	$('[target^="lightbox-gallery"]').each(function() {

		if($(this).attr('data-size') === 'full') {

			GLightbox({
				selector: 'a[target="' + this.attributes.target.value + '"][data-size="full"]',
				openEffect: 'fade',
				closeEffect: 'fade',
				width: 'auto',
				height: 'auto',
				videosWidth: 1280,
				skin: 'fullscreen',
			});
		}
		else {

			GLightbox({
				selector: 'a[target="' + this.attributes.target.value + '"]',
				openEffect: 'fade',
				closeEffect: 'fade',
				videosWidth: 1280,
			});
		}
	});
}

export function loadExternalScript(script) {
	const $script = $(script);
	const src = $script.attr('src');
	$('script[src="' + src + '"]').remove();
	$('body').append($script);
}

export function ajax(options = {}) {

	return new Promise((resolve, reject) => {

		$.ajax(options).done(response => {

			if(response.success) {
				resolve(response);
			}
			else {
				console.error(response);
				reject(response);
			}
		}).fail(error => {
			console.error(error);
			reject(error);
		});
	});
}

export function observeLazyLoadedElements(root = document, loadedClass = Classes.loaded) {

	const isLazy = (el) => el && el.getAttribute && el.getAttribute('loading') === 'lazy';

	const lazyLoaded = (element) => {
		if(!element || !(element instanceof Element)) return;

		if(element.lazyObserved) return;
		element.lazyObserved = true;

		const applyClass = () => {
			element.classList.add(loadedClass);
			cleanup();
			$Elements.window.trigger(Events.animation.refresh);
		};

		const cleanup = () => {
			element.removeEventListener('load', applyClass);
			element.removeEventListener('loadeddata', applyClass);
			element.removeEventListener('error', applyClass);

			setTimeout(() => {
				try { delete element.lazyObserved; }
				catch(e) {}
			}, 0);
		};

		if(element.tagName === 'IMG' || element.tagName === 'IFRAME') {
			if(element.complete && (element.tagName !== 'IMG' || element.naturalWidth !== 0)) {
				applyClass();
			}
			else {
				element.addEventListener('load', applyClass, { once: true });
				element.addEventListener('error', applyClass, { once: true });
			}
			return;
		}

		if(element.tagName === 'VIDEO') {
			if(element.readyState === 4) {
				applyClass();
			}
			else {
				element.addEventListener('loadeddata', applyClass, { once: true });
				element.addEventListener('error', applyClass, { once: true });
			}
			return;
		}

		applyClass();
	};

	const scanInitial = (scope) => {
		const nodeList = (scope || document).querySelectorAll('[loading="lazy"]');
		nodeList.forEach((el) => lazyLoaded(el));
	};

	const observer = new MutationObserver((mutations) => {
		for(const mutation of mutations) {

			if(mutation.addedNodes && mutation.addedNodes.length) {
				mutation.addedNodes.forEach((node) => {
					if(!(node instanceof Element)) return;

					if(isLazy(node)) lazyLoaded(node);
					if(node.querySelectorAll) {
						node.querySelectorAll('[loading="lazy"]').forEach(lazyLoaded);
					}
				});
			}

			if(mutation.type === 'attributes' && mutation.attributeName === 'loading') {
				const target = mutation.target;
				if(isLazy(target)) lazyLoaded(target);
			}
		}
	});

	observer.observe(root, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ['loading'],
	});

	scanInitial(root);

	return {
		observer,
		disconnect() {
			try { observer.disconnect(); }
			catch(e) {}
		}
	};
}

export function loadImage(src) {

	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = reject;
		img.src = src;
	});
}

export function waitForImage(img) {

	return new Promise(resolve => {
		if(img.complete) {
			return resolve();
		}
		img.onload = () => resolve();
		img.onerror = () => resolve();
	});
}

export function waitForVideo(video) {

	return new Promise(resolve => {
		if(video.readyState === 4) {
			return resolve();
		}
		video.addEventListener('loadeddata', () => resolve(), { once: true });
		video.addEventListener('error', () => resolve(), { once: true });
	});
}

export function openMenu() {

	if(!menuScrollLocked) {
		ScrollTrigger.getAll().forEach((st) => st.disable(false, true));
		menuScrollLocked = true;
	}

	$Elements.body.addClass(Classes.menuOpen);
	disableBodyScroll();
}

export function closeMenu() {
	$Elements.body.removeClass(Classes.menuOpen);
	enableBodyScroll();

	if(menuScrollLocked) {
		ScrollTrigger.getAll().forEach((st) => st.enable(false, true));
		ScrollTrigger.refresh();
		menuScrollLocked = false;
	}
}

(r => r.keys().map(r))(require.context('../../svg/', true));
(r => r.keys().map(r))(require.context('../../images/', true, /\.(gif|png|jpe?g|svg)$/));
